import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import ReactTable, { RowInfo } from "react-table";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import { InlineLoading, Modal } from "src/components/common";
import { Link } from "src/components/routing";
import { FontAwesomeIcons } from "src/constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "src/constants/reactTableConstants";
import { ModalSize } from "src/enumerations/ModalSize";
import { TrialInstitutionLaboratoryContractsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import {
    convertToApproximateDateString,
    convertToShortDateString,
} from "src/helpers/date";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as TrialInstitutionLaboratoryActions from "../../../actions/trialInstitutionLaboratory";
import * as TrialInstitutionLaboratoryContractActions from "../../../actions/trialInstitutionLaboratoryContract";
import { RestrictedLayout } from "../../../components/layouts/index";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { TrialInstitutionLabSubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";

import "./TrialInstitutionLaboratoryContractsPage.scss";

interface TrialInstitutionLaboratoryContractsPageProps {
    trialInstitutionLaboratoryContracts: Dtos.TrialInstitutionLaboratoryContract[];
    loadingTrialInstitutionLaboratoryContracts: boolean;
    loadTrialInstitutionLaboratoryContractsSuccess: boolean;
    loadTrialInstitutionLaboratoryContractsFailure: boolean;
    loadTrialInstitutionLaboratoryContractsErrorCode: string;
    trialInstitutionLaboratory: Dtos.TrialInstitutionLaboratory;
    loadingTrialInstitutionLaboratory: boolean;
    loadTrialInstitutionLaboratorySuccess: boolean;
    loadTrialInstitutionLaboratoryFailure: boolean;
    loadTrialInstitutionLaboratoryErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    deletingTrialInstitutionLaboratoryContract: boolean;
    deleteTrialInstitutionLaboratoryContractSuccess: boolean;
    clearTrialInstitutionLaboratoryContracts: TrialInstitutionLaboratoryContractActions.ITrialInstitutionLaboratoryContractClearActionCreator;
    loadTrialInstitutionLaboratoryContracts: TrialInstitutionLaboratoryContractActions.ITrialInstitutionLaboratoryContractLoadByTrialInstitutionLaboratoryIdActionCreator;
    clearTrialInstitutionLaboratory: TrialInstitutionLaboratoryActions.ITrialInstitutionLaboratoryClearActionCreator;
    loadTrialInstitutionLaboratory: TrialInstitutionLaboratoryActions.ITrialInstitutionLaboratoryLoadByIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    deleteTrialInstitutionLaboratoryContract: TrialInstitutionLaboratoryContractActions.ITrialInstitutionLaboratoryContractDeleteActionCreator;
}

const TrialInstitutionLaboratoryContractsPage: FC<
    TrialInstitutionLaboratoryContractsPageProps
> = ({
    trialInstitutionLaboratoryContracts,
    loadingTrialInstitutionLaboratoryContracts,
    loadTrialInstitutionLaboratoryContractsSuccess,
    loadTrialInstitutionLaboratoryContractsFailure,
    loadTrialInstitutionLaboratoryContractsErrorCode,
    trialInstitutionLaboratory,
    loadingTrialInstitutionLaboratory,
    loadTrialInstitutionLaboratorySuccess,
    loadTrialInstitutionLaboratoryFailure,
    loadTrialInstitutionLaboratoryErrorCode,
    permissions,
    pageSize,
    deletingTrialInstitutionLaboratoryContract,
    deleteTrialInstitutionLaboratoryContractSuccess,
    clearTrialInstitutionLaboratoryContracts,
    loadTrialInstitutionLaboratoryContracts,
    clearTrialInstitutionLaboratory,
    loadTrialInstitutionLaboratory,
    setBreadcrumbs,
    setDrawerItems,
    setPageSize,
    deleteTrialInstitutionLaboratoryContract
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { trialInstitutionId } = useParams<{ trialInstitutionId: string }>();
    const { trialInstitutionLaboratoryId } = useParams<{
        trialInstitutionLaboratoryId: string;
    }>();
    const trialIdNumber = parseInt(trialId, 10);
    const trialInstitutionIdNumber = parseInt(trialInstitutionId, 10);
    const trialInstitutionLaboratoryIdNumber = parseInt(
        trialInstitutionLaboratoryId,
        10
    );

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [
        tempTrialInstitutionLaboratoryContract,
        setTempTrialInstitutionLaboratoryContract,
    ] = useState<Dtos.TrialInstitutionLaboratoryContract | undefined>(
        undefined
    );

    //
    // -- Variables --
    const showTrialInstitutionLaboratoryContracts =
        !loadingTrialInstitutionLaboratoryContracts &&
        loadTrialInstitutionLaboratoryContractsSuccess &&
        !loadingTrialInstitutionLaboratory &&
        loadTrialInstitutionLaboratorySuccess;


    // Load Trial institution laboratory & contracts
    useEffect(() => {
        if (
            trialInstitutionLaboratoryIdNumber &&
            !trialInstitutionLaboratoryContracts &&
            !loadingTrialInstitutionLaboratoryContracts
        ) {
            setBreadcrumbs(
                TrialInstitutionLaboratoryContractsBreadcrumbs(
                    trialIdNumber,
                    trialInstitutionIdNumber,
                    trialInstitutionLaboratoryIdNumber
                )
            );

            loadTrialInstitutionLaboratoryContracts(
                trialInstitutionLaboratoryIdNumber
            );
            loadTrialInstitutionLaboratory(trialInstitutionLaboratoryIdNumber);
        }
    }, [
        trialIdNumber,
        trialInstitutionIdNumber,
        trialInstitutionLaboratoryIdNumber,
        trialInstitutionLaboratoryContracts,
        loadingTrialInstitutionLaboratoryContracts,
        setBreadcrumbs,
        loadTrialInstitutionLaboratoryContracts,
        loadTrialInstitutionLaboratory,
        TrialInstitutionLaboratoryContractsBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialInstitutionLaboratoryContractsFailure) {
            navigate(
                `/error/${loadTrialInstitutionLaboratoryContractsErrorCode}`
            );
        }
        if (loadTrialInstitutionLaboratoryFailure) {
            navigate(`/error/${loadTrialInstitutionLaboratoryErrorCode}`);
        }
    }, [
        loadTrialInstitutionLaboratoryContractsFailure,
        loadTrialInstitutionLaboratoryFailure,
        loadTrialInstitutionLaboratoryContractsErrorCode,
        loadTrialInstitutionLaboratoryErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (
            trialInstitutionLaboratoryIdNumber &&
            trialInstitutionLaboratory &&
            trialInstitutionLaboratory?.trialInstitution?.trial &&
            trialInstitutionLaboratory?.trialInstitution?.institution
        ) {
            setDrawerItems(
                TrialInstitutionLabSubMenu(
                    trialInstitutionLaboratory,
                    permissions
                )
            );
            setBreadcrumbs(
                TrialInstitutionLaboratoryContractsBreadcrumbs(
                    trialIdNumber,
                    trialInstitutionIdNumber,
                    trialInstitutionLaboratoryIdNumber,
                    trialInstitutionLaboratory.trialInstitution.trial
                        ?.displayName,
                    trialInstitutionLaboratory.trialInstitution.institution
                        ?.name,
                    trialInstitutionLaboratory.laboratory?.name
                )
            );
        }
    }, [
        trialIdNumber,
        trialInstitutionLaboratory,
        trialInstitutionLaboratoryIdNumber,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        TrialInstitutionLabSubMenu,
        TrialInstitutionLaboratoryContractsBreadcrumbs
    ]);

    // Cleanup effect to clear the trial institution laboratory & contracts on component unmount
    useEffect(() => {
        return () => {
            clearTrialInstitutionLaboratory();
            clearTrialInstitutionLaboratoryContracts();
        };
    }, [
        clearTrialInstitutionLaboratory,
        clearTrialInstitutionLaboratoryContracts,
    ]);

    // Reload trial institution laboratory & contracts after delete
    useEffect(() => {
        if (
            !deletingTrialInstitutionLaboratoryContract &&
            deleteTrialInstitutionLaboratoryContractSuccess
        ) {
            loadTrialInstitutionLaboratory(trialInstitutionLaboratoryIdNumber);
            loadTrialInstitutionLaboratoryContracts(
                trialInstitutionLaboratoryIdNumber
            );
        }
    }, [
        deletingTrialInstitutionLaboratoryContract,
        deleteTrialInstitutionLaboratoryContractSuccess,
        loadTrialInstitutionLaboratory,
        loadTrialInstitutionLaboratoryContracts,
        trialInstitutionLaboratoryIdNumber,
    ]);

    const generateDeleteButton = (
        trialInstitutionLaboratoryContract: Dtos.TrialInstitutionLaboratoryContract
    ) => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.TrialInstitutionLaboratoryContractUpdate,
            undefined,
            undefined
        );

        if (!hasPermission) return null;

        return (
            <button
                key={`contractDeleteButton-${trialInstitutionLaboratoryContract.id}`}
                className="btn btn-outline-secondary btn-sm btn-row-version"
                onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    setTempTrialInstitutionLaboratoryContract(
                        trialInstitutionLaboratoryContract
                    );
                    setOpenModal(true);
                }}
            >
                <FontAwesomeIcon
                    icon={FontAwesomeIcons.Duotone.TRASH}
                    fixedWidth
                />
            </button>
        );
    };

    const createTable = useMemo(() => {
        let tableData = trialInstitutionLaboratoryContracts;

        if (!tableData || tableData?.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no trial institution laboratory
                            contracts to view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination = tableData.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={tableData}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : tableData.length
                    }
                    pageSize={showPagination ? pageSize : tableData.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no trial institution laboratory contracts to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string = `/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/labs/${trialInstitutionLaboratoryIdNumber}/contracts/${rowInfo.original.id}/details`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    defaultSorted={[
                        {
                            id: "name",
                            desc: false,
                        },
                    ]}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Duotone.INSTITUTION}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "name",
                            Header: "Name",
                            accessor: "name",
                        },
                        {
                            id: "dateSentToSite",
                            Header: "Date Sent To Site",
                            accessor: "dateSentToSite",
                            Cell: (props) => (
                                <div>
                                    <span
                                        data-tip={convertToApproximateDateString(
                                            props.value
                                        )}
                                    >
                                        {convertToShortDateString(props.value)}
                                    </span>
                                    <ReactTooltip />
                                </div>
                            ),
                        },
                        {
                            id: "dateConfirmedBySite",
                            Header: "Date Sent To Site",
                            accessor: "dateConfirmedBySite",
                            Cell: (props) => (
                                <div>
                                    <span
                                        data-tip={convertToApproximateDateString(
                                            props.value
                                        )}
                                    >
                                        {convertToShortDateString(props.value)}
                                    </span>
                                    <ReactTooltip />
                                </div>
                            ),
                        },
                        {
                            id: "deleteContract",
                            Header: " ",
                            maxWidth: 75,
                            Cell: (props) => (
                                <div>
                                    {generateDeleteButton(props.original)}
                                </div>
                            ),
                        },
                    ]}
                />
            </div>
        );
    }, [
        trialInstitutionLaboratory,
        trialInstitutionLaboratoryContracts,
        pageSize,
        PAGE_SIZE_OPTIONS,
        navigate,
        setPageSize,
        trialIdNumber,
        trialInstitutionLaboratoryIdNumber,
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-6 mb-2"}>
                        <h1>Contracts</h1>
                    </div>
                    <div className="col-6" style={{ textAlign: "right" }}>
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission
                                .TrialInstitutionLaboratoryContractCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={`/trials/${trialIdNumber}/trialInstitution/${trialInstitutionIdNumber}/labs/${trialInstitutionLaboratoryIdNumber}/contracts/create`}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showTrialInstitutionLaboratoryContracts ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showTrialInstitutionLaboratoryContracts ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        AuthenticationHelper,
        permissions,
        showTrialInstitutionLaboratoryContracts,
        createTable,
        trialIdNumber,
        trialInstitutionLaboratoryIdNumber,
    ]);

    const renderDeleteConfirmation = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.TrialInstitutionLaboratoryContractUpdate,
            undefined,
            undefined
        );

        if (hasPermission) {
            return (
                <Modal
                    open={openModal}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        setOpenModal(false);
                        setTempTrialInstitutionLaboratoryContract(undefined);
                    }}
                    size={ModalSize.Md}
                >
                    <div className="modal-header">
                        <div
                            className="modal-title"
                            style={{ minWidth: "540px" }}
                        >
                            <h2 className="mb-0">Delete</h2>
                        </div>
                        <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() => {
                                setOpenModal(false);
                                setTempTrialInstitutionLaboratoryContract(
                                    undefined
                                );
                            }}
                        >
                            <FontAwesomeIcon
                                fixedWidth
                                icon={FontAwesomeIcons.Regular.TIMES}
                            />
                        </button>
                    </div>
                    <div className="modal-body">
                        <span>
                            Are you sure you want to delete the contract "
                            <em>
                                {tempTrialInstitutionLaboratoryContract?.name}"
                            </em>{" "}
                            from trial institution laboratory{" "}
                            <strong>
                                {
                                    trialInstitutionLaboratory?.trialInstitution
                                        ?.trial?.displayName
                                }{" "}
                                -{" "}
                                {
                                    trialInstitutionLaboratory?.trialInstitution
                                        ?.institution?.name
                                }
                            </strong>
                            .
                        </span>
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => {
                                deleteTrialInstitutionLaboratoryContract(
                                    tempTrialInstitutionLaboratoryContract?.id
                                );
                                setOpenModal(false);
                                setTempTrialInstitutionLaboratoryContract(
                                    undefined
                                );
                            }}
                        >
                            Delete
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => {
                                setOpenModal(false);
                                setTempTrialInstitutionLaboratoryContract(
                                    undefined
                                );
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </Modal>
            );
        }
        return null;
    }, [
        trialInstitutionLaboratory,
        tempTrialInstitutionLaboratoryContract,
        openModal,
        permissions,
        setOpenModal,
        setTempTrialInstitutionLaboratoryContract,
        trialInstitutionLaboratoryIdNumber,
        loadTrialInstitutionLaboratoryContracts,
        deleteTrialInstitutionLaboratoryContract,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialInstitutionLaboratoryContractView}
            loading={
                loadingTrialInstitutionLaboratoryContracts ||
                loadingTrialInstitutionLaboratory
            }
        >
            {renderContent}
            {renderDeleteConfirmation}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialInstitutionLaboratoryContractsPageProps> => {
    let trialInstitutionLaboratoryContracts:
        | Dtos.TrialInstitutionLaboratoryContract[]
        | undefined =
        state.trialInstitutionLaboratoryContract.formData instanceof Array
            ? state.trialInstitutionLaboratoryContract.formData
            : undefined;
    let trialInstitutionLaboratory:
        | Dtos.TrialInstitutionLaboratory
        | undefined = !(
        state.trialInstitutionLaboratory.formData instanceof Array
    )
        ? state.trialInstitutionLaboratory.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialInstitutionLaboratoryContracts:
            trialInstitutionLaboratoryContracts!,
        loadingTrialInstitutionLaboratoryContracts:
            state.trialInstitutionLaboratoryContract.loadState &&
            state.trialInstitutionLaboratoryContract.loadState.status ==
                RequestState.Pending,
        loadTrialInstitutionLaboratoryContractsSuccess:
            state.trialInstitutionLaboratoryContract.loadState &&
            state.trialInstitutionLaboratoryContract.loadState.status ==
                RequestState.Success,
        loadTrialInstitutionLaboratoryContractsFailure:
            state.trialInstitutionLaboratoryContract.loadState &&
            state.trialInstitutionLaboratoryContract.loadState.status ==
                RequestState.Failure,
        loadTrialInstitutionLaboratoryContractsErrorCode:
            state.trialInstitutionLaboratoryContract.loadState.errorCode,
        trialInstitutionLaboratory: trialInstitutionLaboratory!,
        loadingTrialInstitutionLaboratory:
            state.trialInstitutionLaboratory.loadState &&
            state.trialInstitutionLaboratory.loadState.status ==
                RequestState.Pending,
        loadTrialInstitutionLaboratorySuccess:
            state.trialInstitutionLaboratory.loadState &&
            state.trialInstitutionLaboratory.loadState.status ==
                RequestState.Success,
        loadTrialInstitutionLaboratoryFailure:
            state.trialInstitutionLaboratory.loadState &&
            state.trialInstitutionLaboratory.loadState.status ==
                RequestState.Failure,
        loadTrialInstitutionLaboratoryErrorCode:
            state.trialInstitutionLaboratory.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
        deletingTrialInstitutionLaboratoryContract:
            state.trialInstitutionLaboratoryContract.deleteState &&
            state.trialInstitutionLaboratoryContract.deleteState.status ==
                RequestState.Pending,
        deleteTrialInstitutionLaboratoryContractSuccess:
            state.trialInstitutionLaboratoryContract.deleteState &&
            state.trialInstitutionLaboratoryContract.deleteState.status ==
                RequestState.Success,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<TrialInstitutionLaboratoryContractsPageProps> => {
    return {
        loadTrialInstitutionLaboratoryContracts: bindActionCreators(
            TrialInstitutionLaboratoryContractActions.LoadTrialInstitutionLaboratoryContractByTrialInstitutionLaboratoryId,
            dispatch
        ),
        clearTrialInstitutionLaboratoryContracts: bindActionCreators(
            TrialInstitutionLaboratoryContractActions.Clear,
            dispatch
        ),
        loadTrialInstitutionLaboratory: bindActionCreators(
            TrialInstitutionLaboratoryActions.LoadTrialInstitutionLaboratoryById,
            dispatch
        ),
        clearTrialInstitutionLaboratory: bindActionCreators(
            TrialInstitutionLaboratoryActions.Clear,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),
        deleteTrialInstitutionLaboratoryContract: bindActionCreators(
            TrialInstitutionLaboratoryContractActions.DeleteTrialInstitutionLaboratoryContract,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialInstitutionLaboratoryContractsPage);
